/**
 * Routing.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  CONTRACT FOR PAGE AUTHORS
 * ─────────────────────────────────────────────────────────────────────
 *
 * Drop a file into `src/pages/` with a **default export** of a React
 * component and its route lights up. Accepted filenames per route are
 * listed in `ROUTES` below — any one of them works, so
 * `src/pages/Dashboard.tsx`, `src/pages/DashboardPage.tsx` and
 * `src/pages/dashboard/index.tsx` are all fine.
 *
 * Pages are discovered with `import.meta.glob`, code-split per route, and
 * a route whose file does not exist yet renders an honest "not built yet"
 * panel instead of breaking the build. That is what lets three agents work
 * on this app at the same time without blocking each other.
 *
 * A page renders its *content only*. The shell — top bar, rail, mobile nav,
 * LIVE banner, toasts — is supplied here. To put controls in the top bar,
 * render them into `<TopBarSlot>`; the shell picks them up.
 *
 * Available to every page:
 *   `@/api/hooks`          one hook per endpoint, typed
 *   `@/api/ws`             the shared websocket, reference-counted topics
 *   `@/components/ui`      the design system
 *   `@/components/chart`   PriceChart + DrawingToolbar
 *   `@/store/uiStore`      symbol, timeframe, layout, cross-link selection
 *   `@/lib/format`         every formatter; nothing formats inline
 *   `@/lib/colors`         every semantic colour decision
 */

import { Hammer } from 'lucide-react';
import {
  Suspense,
  lazy,
  useEffect,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthGate, AuthProvider } from './auth/AuthProvider';
import { AppShell } from './components/layout/AppShell';
import { Button, EmptyState, SkeletonChart } from './components/ui';

// ══════════════════════════════════════════════════════════════════════
//  Page discovery
// ══════════════════════════════════════════════════════════════════════

type PageModule = { default: ComponentType };

const PAGE_MODULES = import.meta.glob<PageModule>('./pages/**/*.tsx');

interface RouteSpec {
  path: string;
  title: string;
  /** Filenames (without `./pages/`) that satisfy this route, in priority order. */
  candidates: readonly string[];
  /** Full-bleed: the page owns its own scrolling and padding. */
  flush?: boolean;
}

const ROUTES: readonly RouteSpec[] = [
  {
    path: '/',
    title: 'Dashboard',
    candidates: ['Dashboard.tsx', 'DashboardPage.tsx', 'dashboard/index.tsx'],
  },
  {
    path: '/markets',
    title: 'Markets',
    candidates: ['Markets.tsx', 'MarketsPage.tsx', 'markets/index.tsx'],
    flush: true,
  },
  {
    path: '/backtest/*',
    title: 'Backtest',
    // `BacktestLab.tsx` is the name the page was actually written under.
    // It is listed first because it is the file that exists; the others are
    // kept so the documented convention still resolves.
    candidates: [
      'BacktestLab.tsx',
      'Backtest.tsx',
      'BacktestPage.tsx',
      'backtest/index.tsx',
    ],
    flush: true,
  },
  {
    path: '/strategies/*',
    title: 'Deploy',
    candidates: ['Strategies.tsx', 'StrategiesPage.tsx', 'strategies/index.tsx'],
  },
  {
    path: '/portfolio/*',
    title: 'Portfolio',
    candidates: ['Portfolio.tsx', 'PortfolioPage.tsx', 'portfolio/index.tsx'],
  },
  {
    path: '/monitoring/*',
    title: 'Monitoring',
    candidates: ['Monitoring.tsx', 'MonitoringPage.tsx', 'monitoring/index.tsx'],
  },
  {
    path: '/settings/*',
    title: 'Settings',
    candidates: ['Settings.tsx', 'SettingsPage.tsx', 'settings/index.tsx'],
  },
  {
    path: '/database',
    title: 'Database',
    candidates: ['Database.tsx', 'DatabasePage.tsx', 'database/index.tsx'],
    // `flush` because the page owns two scrollers of its own — the table list
    // and the row grid. Without it the shell's `overflow-y-auto` wraps them
    // both and the grid's sticky header detaches from the rows it labels.
    flush: true,
  },
  {
    path: '/documentation/*',
    title: 'Documentation',
    candidates: ['Documentation.tsx', 'DocumentationPage.tsx', 'documentation/index.tsx'],
    flush: true,
  },
] as const;

function resolvePage(candidates: readonly string[]): ComponentType | null {
  for (const candidate of candidates) {
    const loader = PAGE_MODULES[`./pages/${candidate}`];
    if (loader) return lazy(loader);
  }
  return null;
}

/**
 * Every route resolved **once**, at module load.
 *
 * `lazy()` returns a *new component identity* on every call. Resolving
 * inside `App`'s render therefore handed React a different component type
 * for the same route on every render — and React unmounts and remounts a
 * subtree whose element type changed. The page would lose all of its local
 * state (open dock tab, scroll position, a half-filled backtest form) any
 * time an ancestor re-rendered, and StrictMode's double render made it
 * happen on the very first paint.
 *
 * Doing it here also means the "page not written yet" check runs once
 * instead of per render, and the map is a static description of what the
 * app actually routes to — see `unroutedPaths` below.
 */
const RESOLVED_PAGES: ReadonlyMap<string, ComponentType | null> = new Map(
  ROUTES.map((route) => [route.path, resolvePage(route.candidates)]),
);

/**
 * Routes whose page file does not exist.
 *
 * A missing page renders an honest panel rather than breaking the build,
 * which is what lets several agents work on this app at once — but it also
 * means a filename typo is invisible until someone clicks the tab. That has
 * already cost this project a whole page once (BacktestLab), so in dev the
 * mismatch is shouted at the console on boot.
 */
export function unroutedPaths(): string[] {
  return ROUTES.filter((route) => RESOLVED_PAGES.get(route.path) == null).map(
    (route) => `${route.path} -> src/pages/${route.candidates[0]}`,
  );
}

if (import.meta.env.DEV) {
  const missing = unroutedPaths();
  if (missing.length > 0) {
    console.error(
      `[routing] ${missing.length} route(s) resolve to no page file:\n  ${missing.join('\n  ')}`,
    );
  }
}

/**
 * Placeholder for a route whose page has not been written yet.
 *
 * Names the exact file to create, because "404" tells a developer nothing
 * and this app is being built by several people at once.
 */
function NotBuiltYet({ title, candidates }: { title: string; candidates: readonly string[] }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <EmptyState
        icon={Hammer}
        title={`${title} has not been built yet`}
        description={
          <span>
            Create{' '}
            <code className="num rounded bg-surface-2 px-1 text-hi">
              src/pages/{candidates[0]}
            </code>{' '}
            with a default-exported React component and this route will pick it up
            automatically. The shell, API hooks, chart and design system are already in place.
          </span>
        }
      />
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SkeletonChart />
    </div>
  );
}

function UnknownRoute() {
  const location = useLocation();
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <EmptyState
        title="No such page"
        description={
          <>
            Nothing is routed at <code className="num">{location.pathname}</code>.
          </>
        }
        action={
          <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
            Go back
          </Button>
        }
      />
    </div>
  );
}

/**
 * The shell wraps the routed content rather than each page, so the top bar,
 * the LIVE banner and the websocket cache sync survive navigation instead
 * of remounting — which would drop the socket subscriptions on every click.
 */
function RouteShell({ flush, children }: { flush?: boolean; children: ReactNode }) {
  return (
    <AppShell flush={flush ?? false}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </AppShell>
  );
}

function AuthedApp() {
  // Prewarm the chart-heavy Markets route during idle, so opening it does not
  // pay the klinecharts chunk's download+compile on the click. The chart draws
  // in ~40ms once its code is loaded (PriceChart's [chart-perf] marks); the
  // one-time cost is loading that code, and this moves it off the critical path
  // onto idle time after first paint. Runs only when the browser is idle, so it
  // never competes with the landing route's own render.
  useEffect(() => {
    let cancelled = false;
    const warm = (): void => {
      if (cancelled) return;
      PAGE_MODULES['./pages/Markets.tsx']?.().catch(() => {
        /* prewarm is best-effort; a failure just means no head start */
      });
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(warm, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(warm, 1000);
    }
    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Routes>
      {ROUTES.map((route) => {
        const Page = RESOLVED_PAGES.get(route.path) ?? null;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <RouteShell flush={route.flush ?? false}>
                {Page ? (
                  <Page />
                ) : (
                  <NotBuiltYet title={route.title} candidates={route.candidates} />
                )}
              </RouteShell>
            }
          />
        );
      })}

      {/* Legacy/deep links land on the dashboard rather than a dead end. */}
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route
        path="*"
        element={
          <RouteShell>
            <UnknownRoute />
          </RouteShell>
        }
      />
    </Routes>
  );
}

/**
 * The login gate sits ABOVE the shell: an unauthenticated owner sees only the
 * login screen, never the nav or a protected call. `AuthProvider` is inside the
 * router and query providers (main.tsx), so it can re-check the session and
 * invalidate the cache on login/logout.
 */
export default function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <AuthedApp />
      </AuthGate>
    </AuthProvider>
  );
}

export { ROUTES };
export type { RouteSpec };
